import React, { FC } from "react";
import { Dropdown } from "react-bootstrap";
import CheckoutCustomColorSection from "./CheckoutCustomColorSection";
import CheckoutBasicColorSection from "./CheckoutBasicColorSection";
import { observer } from "mobx-react";
import CheckoutDonationOptions from "./CheckoutDonationOptions"; 
import {
  CustomLabel,
  FlexContainer,
  IconButton,
  Input,
  IOption,
  SingleSelect,
} from "@mindmemobile/mm-shared-global";
import { CheckOutStore } from "../../../shared/store";
import { BasicColors, CheckoutBrandingModel, CheckoutOptionsPageModel, colorTypes, CountryWithStateModel, CustomColors } from "../../../shared/models";
import { CheckoutColorType, CheckOutHelpType } from "../../../shared/enums";
import { CountryFlag, DropdownWrapper, OptionsDropdownContainer, StyledDropdownButton, StyledInput } from "../../../shared/styles";


export type ColorPickerType = 'main' | 'border' | 'background' | 'progress';
type IProps = {
    checkoutStore: CheckOutStore;
    onChange: (field: keyof CheckoutOptionsPageModel, value: CheckoutBrandingModel) => void;
};

const sellerContactOptions: IOption[] = Object.keys(CheckOutHelpType).map((key) => {
    return {
        label: key,
        value: CheckOutHelpType[key as keyof typeof CheckOutHelpType],
    };
});

const CheckoutBrandingSection: FC<IProps> = ({ onChange, checkoutStore }) => {
    const { brandingData } = checkoutStore.optionsPage;

    const checkBoxInput = (key: keyof CheckoutBrandingModel, value: boolean, label: string, disabled: boolean) => {
        return (
            <Input
                type="checkbox"
                value={value}
                onChange={(e) => handleBrandingInputChange(key, e.target.checked)}
                label={label}
                disabled={disabled || false}
            />
        );
    };

    const singleSelect = (
        field: keyof CheckoutBrandingModel,
        selectedKey: string,
        className: string,
        options: IOption[],
        error: boolean,
        errorMessage: string,
    ): React.ReactNode => {
        return (
            <SingleSelect
                selectedKey={selectedKey}
                hasError={error}
                searchable={false}
                nameKeys={['label']}
                valueKeys={['value']}
                options={[options || []]}
                errorMessage={errorMessage}
                onChange={(option: any) => handleBrandingInputChange(field, option.value)}
                className={className}
            />
        );
    };

    const textInput = (
        field: keyof CheckoutBrandingModel,
        type: string,
        value: string,
        placeHolder: string,
        className: string,
        error: boolean,
        errorMessage: string,
    ) => {
        return (
            <Input
                type={'text'}
                value={value}
                error={error}
                errorMessage={errorMessage}
                className={`styled-input merchant-inputs ${className}`}
                onChange={(e) => handleBrandingInputChange(field, e.target.value)}
                placeholder={placeHolder}
            />
        );
    };

    const handleSelect = (id: string | null) => {
        if (id) {
            const country = checkoutStore.countryStore.countriesWithState.find((country) => country.id === id);
            if (country) {
                handleBrandingInputChange('countryDetails', country);
            }
        }
    };

    const renderCountryDropdown = (
        selectedCountry: CountryWithStateModel,
        error: boolean,
        errorMessage: string,
        className?: string,
    ) => {
        const { countriesWithState } = checkoutStore.countryStore;

        const selectedCountryIndex = countriesWithState.findIndex(
            (country: CountryWithStateModel) => country.id === selectedCountry.id,
        );

        const flagLink = selectedCountryIndex >= 0 ? countriesWithState[selectedCountryIndex]?.flagURL : '';
        return (
            <OptionsDropdownContainer className={className}>
                <DropdownWrapper error={error.toString()}>
                    <StyledDropdownButton
                        id="dropdown-basic-button"
                        title={
                            selectedCountry.id ? (
                                <FlexContainer alignItems="center" className="styled-dropdown-flex">
                                    <CountryFlag className="country-flag" src={flagLink} alt={selectedCountry.name} />
                                    <CustomLabel className="custom-label" fontSize="large" fontWeight="light">
                                        +{selectedCountry.code}
                                    </CustomLabel>
                                </FlexContainer>
                            ) : (
                                <CustomLabel fontSize="large" fontWeight="light" className="phone-data">
                                    Select
                                </CustomLabel>
                            )
                        }
                        onSelect={(eventKey: string | null) => handleSelect(eventKey)}
                        variant="light"
                    >
                        {checkoutStore.countryStore.countriesWithState.map((country) => (
                            <Dropdown.Item key={country.id} eventKey={country.id}>
                                <CountryFlag src={country.flagURL} alt={country.name} />
                                <span>
                                    +{country.code} {country.name}
                                </span>
                            </Dropdown.Item>
                        ))}
                    </StyledDropdownButton>
                    <StyledInput
                        type="text"
                        placeholder="Enter number"
                        value={brandingData.phoneNumber}
                        onChange={(e) => {
                            handleBrandingInputChange('phoneNumber', e.target.value);
                        }}
                    />
                </DropdownWrapper>
                {error && <p className="error-message">{errorMessage}</p>}
            </OptionsDropdownContainer>
        );
    };

    const handleBrandingInputChange = (
        field: keyof CheckoutBrandingModel,
        value: string | boolean | Date | CountryWithStateModel | CustomColors | BasicColors,
    ) => {
        const updatedBrandingData = new CheckoutBrandingModel({ ...brandingData, [field]: value });
        onChange('brandingData', updatedBrandingData);
    };

    return (
        <FlexContainer fxLayout="column" spacing={30} className="option-checkout">
            <CustomLabel className="checkout-headers">Seller</CustomLabel>
            <div>
                <FlexContainer fxLayout="column" spacing={20}>
                    {checkBoxInput('showLogo', brandingData.showLogo, 'Show Logo', false)}
                    {checkBoxInput('showHelp', brandingData.showHelp, 'Show Help', false)}
                </FlexContainer>
            </div>
            {brandingData.showHelp && (
                <div>
                    {' '}
                    <FlexContainer className="chckout-align-space" spacing={20}>
                        {singleSelect(
                            'sellerContact',
                            brandingData.sellerContact,
                            'branding-help-num dpdn-no-separator',
                            sellerContactOptions,
                            false,
                            '',
                        )}
                        {brandingData.sellerContact === CheckOutHelpType.Phone
                            ? renderCountryDropdown(
                                  brandingData.countryDetails,
                                  brandingData.hasError('phoneNumber'),
                                  '',
                                  'branding-country-dropdown',
                              )
                            : textInput(
                                  'helpEmail',
                                  'text',
                                  brandingData.helpEmail,
                                  'Enter email',
                                  'email-input',
                                  brandingData.hasError('helpEmail'),
                                  '',
                              )}
                    </FlexContainer>
                </div>
            )}

            <FlexContainer fxLayout="column" spacing={30}>
                <CustomLabel className="checkout-headers">Design</CustomLabel>
                {!checkoutStore.detailPage.isDonationOnly ? (
                    <>
                        <div>
                            <FlexContainer spacing={20} alignItems="center">
                                {colorTypes.map((colorType) => (
                                    <>
                                        {/* <Button
                                    key={colorType.name}
                                    color={getBgColor(colorType.name)}
                                    borderColor={getBgColor(colorType.name)}
                                    onClick={() => handleBrandingInputChange('selectedColorType', colorType.name)}
                                >
                                    {colorType.name === CheckoutColorType.CustomColor ? 'Custom' : 'Basic'}
                                </Button> */}
                                        <IconButton
                                            className={`blue-btn-green-hovered ${colorType.name === brandingData.selectedColorType ? 'active' : ''}`}
                                            key={colorType.name}
                                            onClick={() =>
                                                handleBrandingInputChange('selectedColorType', colorType.name)
                                            }
                                        >
                                            <img src={colorType.icon} alt="displayicon" />
                                            <span>
                                                {' '}
                                                {colorType.name === CheckoutColorType.CustomColor ? 'Custom' : 'Basic'}
                                            </span>
                                        </IconButton>
                                    </>
                                ))}
                            </FlexContainer>
                        </div>
                        {brandingData.selectedColorType === CheckoutColorType.CustomColor ? (
                            <CheckoutCustomColorSection
                                customColors={brandingData.customColors}
                                onChange={(field: keyof CheckoutBrandingModel, value: CustomColors) =>
                                    handleBrandingInputChange(field, value)
                                }
                            />
                        ) : (
                            <CheckoutBasicColorSection
                                basicColors={brandingData.basicColors}
                                onChange={(field: keyof CheckoutBrandingModel, value: BasicColors) =>
                                    handleBrandingInputChange(field, value)
                                }
                            />
                        )}
                    </>
                ) : (
                    <CheckoutDonationOptions
                        donationOptions={checkoutStore.optionsPage.brandingData.donationCheckout}
                        onChange={(field: keyof CheckoutBrandingModel, value: any) =>
                            handleBrandingInputChange(field, value)
                        }
                    />
                )}
            </FlexContainer>
        </FlexContainer>
    );
};

export default observer(CheckoutBrandingSection);
